// Iter 82.3 / Phase E1.3 — Manager Workspace.
// Action-oriented landing page for managers:
//   • Pending approval queue (their direct reports' submissions)
//   • Availability planning: On leave today / tomorrow / ending today
//   • Upcoming approved (next 14 days)
//   • Team utilization mini snapshot
// Powered by /api/leave/workspace/manager.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Users, CalendarCheck, ClockCountdown, ArrowFatLineRight,
    UserMinus, Sun, Alarm, ChartBar,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

function StatCard({ icon, label, value, tone = "slate", testid }) {
    const map = {
        slate: "bg-slate-50 border-slate-200 text-slate-700",
        amber: "bg-amber-50 border-amber-200 text-amber-900",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
        sky: "bg-sky-50 border-sky-200 text-sky-900",
        rose: "bg-rose-50 border-rose-200 text-rose-900",
        violet: "bg-violet-50 border-violet-200 text-violet-900",
    };
    return (
        <div className={`rounded-xl border p-4 ${map[tone]}`} data-testid={testid}>
            <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-wider font-semibold">{label}</div>
                {icon}
            </div>
            <div className="text-3xl font-bold mt-2">{value}</div>
        </div>
    );
}

export default function ManagerWorkspace() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data: payload } = await api.get("/leave/workspace/manager");
            setData(payload);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load workspace"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { Promise.resolve().then(reload); }, [reload]);

    if (loading || !data) {
        return (
            <div className="px-6 sm:px-8 py-6" data-testid="manager-workspace-loading">
                <PageHeader eyebrow="Leave Operations" title="Manager Workspace" />
                <div className="text-sm text-slate-400 py-10 text-center">Loading…</div>
            </div>
        );
    }

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1500px]" data-testid="manager-workspace-page">
            <PageHeader
                eyebrow="Leave Operations"
                title="Manager Workspace"
                description={`${data.direct_reports_count} direct report${data.direct_reports_count === 1 ? "" : "s"} · ${data.pending_count} request${data.pending_count === 1 ? "" : "s"} awaiting your decision`}
                actions={(
                    <div className="flex gap-2">
                        <Link to="/leave/requests?scope=pending-mine">
                            <Button variant="outline" size="sm" data-testid="mw-pending-btn">
                                <CalendarCheck size={14} className="mr-1" /> Pending My Approval
                            </Button>
                        </Link>
                        <Link to="/leave/calendar">
                            <Button variant="outline" size="sm" data-testid="mw-calendar-btn">
                                <Sun size={14} className="mr-1" /> Team Calendar
                            </Button>
                        </Link>
                    </div>
                )}
            />

            <section className="mt-5 grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="mw-stats">
                <StatCard testid="mw-stat-pending" icon={<ClockCountdown size={18} />} label="Pending Decisions" value={data.pending_count} tone="amber" />
                <StatCard testid="mw-stat-today" icon={<UserMinus size={18} />} label="On Leave Today" value={data.on_leave_today.length} tone="rose" />
                <StatCard testid="mw-stat-tomorrow" icon={<Alarm size={18} />} label="On Leave Tomorrow" value={data.on_leave_tomorrow.length} tone="violet" />
                <StatCard testid="mw-stat-ending" icon={<ArrowFatLineRight size={18} />} label="Returning Today" value={data.ending_today.length} tone="emerald" />
                <StatCard testid="mw-stat-upcoming" icon={<Users size={18} />} label={`Approved in Next ${data.horizon_days}d`} value={data.upcoming_approved.length} tone="sky" />
            </section>

            <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <section className="lg:col-span-2 bg-white rounded-xl border border-slate-200" data-testid="mw-pending-section">
                    <div className="px-5 py-3 border-b border-slate-100 flex justify-between items-center">
                        <h3 className="text-sm font-semibold text-slate-800">Pending Approvals</h3>
                        {data.pending_approvals.length > 5 && (
                            <Link to="/leave/requests?scope=pending-mine" className="text-xs text-slate-500 hover:underline">View all {data.pending_approvals.length} →</Link>
                        )}
                    </div>
                    {data.pending_approvals.length === 0 ? (
                        <div className="text-center py-10 text-sm text-slate-400">All caught up. Nothing pending.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600 text-xs">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Employee</th>
                                    <th className="text-left px-3 py-2 font-medium">Type</th>
                                    <th className="text-left px-3 py-2 font-medium">Period</th>
                                    <th className="text-right px-3 py-2 font-medium">Days</th>
                                    <th className="text-right px-3 py-2 font-medium" title="Provisional split at approval — authoritative split at payroll freeze">Split (advisory)</th>
                                    <th className="text-left px-3 py-2 font-medium">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.pending_approvals.slice(0, 5).map((r) => {
                                    // P0-A Batch 2 · surface provisional Paid/LOP split.
                                    // The backend stamps these on the leave request when
                                    // moving from Draft → Pending (see leave_reservations
                                    // .compute_provisional_split). Fall back to full-paid
                                    // if the request predates the split model.
                                    const paid = Number(r.provisional_paid_days ?? r.days_total ?? 0);
                                    const lop = Number(r.provisional_lop_days ?? 0);
                                    return (
                                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                                        onClick={() => window.location.assign(`/leave/requests/${r.id}`)}
                                        data-testid={`mw-pending-row-${r.id}`}>
                                        <td className="px-3 py-2">
                                            <div className="font-medium text-slate-800">{r.employee_name}</div>
                                            <div className="text-xs text-slate-500 font-mono">{r.employee_code}</div>
                                        </td>
                                        <td className="px-3 py-2 font-mono text-xs">{r.leave_type_code}</td>
                                        <td className="px-3 py-2 text-xs">{formatDate(r.from_date)} → {formatDate(r.to_date)}</td>
                                        <td className="px-3 py-2 text-right font-semibold">{Number(r.days_total).toFixed(1)}</td>
                                        <td className="px-3 py-2 text-right text-xs font-mono" data-testid={`mw-pending-split-${r.id}`}>
                                            <span className="text-emerald-800">{paid.toFixed(1)}</span>
                                            <span className="text-slate-400"> · </span>
                                            <span className={lop > 0 ? "text-amber-800" : "text-slate-400"}>{lop.toFixed(1)}</span>
                                            <div className="text-[9px] text-slate-400 leading-none">Paid · LOP</div>
                                        </td>
                                        <td className="px-3 py-2"><Badge className="bg-amber-100 text-amber-800 text-xs">{r.status}</Badge></td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </section>

                <aside className="bg-white rounded-xl border border-slate-200" data-testid="mw-availability">
                    <div className="px-5 py-3 border-b border-slate-100">
                        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                            <Users size={14} /> Upcoming Team Availability
                        </h3>
                    </div>
                    <div className="p-5 space-y-4 text-sm">
                        <div data-testid="mw-today-block">
                            <div className="text-[10px] uppercase tracking-wider text-rose-700 font-semibold mb-1">On Leave Today</div>
                            {data.on_leave_today.length === 0 ? (
                                <div className="text-xs text-slate-400 italic">No one on leave today.</div>
                            ) : (
                                <ul className="space-y-1">
                                    {data.on_leave_today.map((r) => (
                                        <li key={r.id} className="text-xs flex justify-between" data-testid={`mw-today-${r.id}`}>
                                            <span className="font-medium text-slate-800">{r.employee_name}</span>
                                            <span className="text-slate-500">{r.leave_type_code} · {formatDate(r.to_date)}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div data-testid="mw-tomorrow-block">
                            <div className="text-[10px] uppercase tracking-wider text-violet-700 font-semibold mb-1">On Leave Tomorrow</div>
                            {data.on_leave_tomorrow.length === 0 ? (
                                <div className="text-xs text-slate-400 italic">No one on leave tomorrow.</div>
                            ) : (
                                <ul className="space-y-1">
                                    {data.on_leave_tomorrow.map((r) => (
                                        <li key={r.id} className="text-xs flex justify-between" data-testid={`mw-tomorrow-${r.id}`}>
                                            <span className="font-medium text-slate-800">{r.employee_name}</span>
                                            <span className="text-slate-500">{r.leave_type_code} · {formatDate(r.to_date)}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div data-testid="mw-ending-block">
                            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">Returning Today (Leave Ends)</div>
                            {data.ending_today.length === 0 ? (
                                <div className="text-xs text-slate-400 italic">No one returning today.</div>
                            ) : (
                                <ul className="space-y-1">
                                    {data.ending_today.map((r) => (
                                        <li key={r.id} className="text-xs flex justify-between" data-testid={`mw-ending-${r.id}`}>
                                            <span className="font-medium text-slate-800">{r.employee_name}</span>
                                            <span className="text-slate-500">{r.leave_type_code}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div data-testid="mw-upcoming-block">
                            <div className="text-[10px] uppercase tracking-wider text-sky-700 font-semibold mb-1">Approved in Next {data.horizon_days} Days</div>
                            {data.upcoming_approved.length === 0 ? (
                                <div className="text-xs text-slate-400 italic">No upcoming approved leaves.</div>
                            ) : (
                                <ul className="space-y-1">
                                    {data.upcoming_approved.slice(0, 8).map((r) => (
                                        <li key={r.id} className="text-xs flex justify-between" data-testid={`mw-upcoming-${r.id}`}>
                                            <span className="font-medium text-slate-800">{r.employee_name}</span>
                                            <span className="text-slate-500">{formatDate(r.from_date)} ({Number(r.days_total).toFixed(1)}d)</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </aside>
            </div>

            <section className="mt-5 bg-white rounded-xl border border-slate-200" data-testid="mw-utilization">
                <div className="px-5 py-3 border-b border-slate-100">
                    <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                        <ChartBar size={14} /> Team Utilization · FY {data.fiscal_year}
                    </h3>
                </div>
                {data.team_utilization.length === 0 ? (
                    <div className="text-center py-8 text-sm text-slate-400">No direct reports with leave balances configured.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Employee</th>
                                <th className="text-right px-3 py-2 font-medium">Entitled</th>
                                <th className="text-right px-3 py-2 font-medium">Taken</th>
                                <th className="text-right px-3 py-2 font-medium">Current Balance</th>
                                <th className="text-right px-3 py-2 font-medium">Utilization %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.team_utilization.map((u) => (
                                <tr key={u.employee_id} className="border-t border-slate-100" data-testid={`mw-util-${u.employee_id}`}>
                                    <td className="px-3 py-2">
                                        <div className="font-medium text-slate-800">{u.employee_name}</div>
                                        <div className="text-xs text-slate-500 font-mono">{u.employee_code}</div>
                                    </td>
                                    <td className="px-3 py-2 text-right">{u.entitled.toFixed(1)}</td>
                                    <td className="px-3 py-2 text-right text-rose-700">{u.taken_ytd.toFixed(1)}</td>
                                    <td className="px-3 py-2 text-right font-semibold">{u.current_balance.toFixed(1)}</td>
                                    <td className="px-3 py-2 text-right">
                                        <Badge className={`text-xs ${u.utilization_pct > 75 ? "bg-rose-100 text-rose-800" : u.utilization_pct > 50 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                                            {u.utilization_pct.toFixed(0)}%
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
        </div>
    );
}
